import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { type FC, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form@latest';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { errorToast, successToast } from '@/helpers';
import { _notNil } from '@/littledash';

import InVivoError from '@/model/InVivoError';
import type { StudyFormMetaDetails, StudyFormSection } from '@/model/StudyForm.model';

import { studyTypes } from '@/referenceData/study/type';
import Http from '@/support/http';
import { api as apiRoute, web as webRoute } from '@/support/route';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Button from '@/components/UI/Button';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import Radio from '@/components/UI/FormElements/Radio';
import DraggableList, { asSortableListItem } from '@/components/UI/List/DraggableList';
import Tooltip from '@/components/UI/Tooltip/Tooltip';

interface StudyTemplateFormProps {
  template: StudyFormMetaDetails;
  onSaveTemplate: () => void;
  onSaveSection: () => void;
}

interface StudyTemplateFormData {
  name: string;
  type: 'internal' | 'external';
  sections: Array<{ value: StudyFormSection }>;
}

const StudyTemplateForm: FC<StudyTemplateFormProps> = ({ template, onSaveTemplate, onSaveSection }) => {
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<unknown>(null);
  const params = useParams();
  const dispatch = useDispatch();
  const defaultValues = useMemo(
    () => ({
      name: template.name,
      type: template.type || 'internal',
      sections: template.sections.map((section) => ({
        value: {
          ...section,
        },
      })),
    }),
    [template]
  );
  const formMethods = useForm<StudyTemplateFormData>({
    defaultValues,
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = formMethods;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const onSubmit = async (data: StudyTemplateFormData) => {
    setSubmitting(true);
    try {
      await Http.put(apiRoute('team.studies.forms.update', { id: template.id }), {
        ...data,
        sections: data.sections.map((d) => ({ ...d.value })),
      });
      successToast('Saved form.');
      onSaveTemplate();
    } catch (error) {
      setApiError(error);
      errorToast('There was a problem with your submission.');
      ExceptionHandler.captureException(
        new InVivoError('Failed to save study template form', {
          cause: error,
          slug: 'study-template-form',
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const { fields, move: moveFormItems } = useFieldArray({
    control,
    name: 'sections',
  });

  const openSectionModal = (sectionId: string | false = false) => {
    const customSectionNames = fields.reduce<Array<string>>((acc, field) => {
      if (field.value.type === 'custom') {
        acc.push(field.value.name);
      }
      return acc;
    }, []);
    const props: Record<string, unknown> = {
      formId: params.formId,
      onSaveSection,
      saveTemplate: () => handleSubmit(onSubmit)(),
      customSectionNames,
    };

    if (sectionId !== false) {
      props.sectionId = sectionId;
    }

    dispatch({
      type: 'OPEN_MODAL',
      modal: 'CREATE_STUDY_FORM_SECTION',
      props,
    });
  };

  const deleteSection = async (id: string) => {
    try {
      await Http.delete(apiRoute('team.studies.forms.section.delete', { formId: template.id, sectionId: id }));
      successToast('Deleted section.');
      onSaveSection();
    } catch (error) {
      setApiError(error);
      errorToast('There was a problem deleting the section.');
      ExceptionHandler.captureException(
        new InVivoError('Failed to delete study form section', {
          cause: error,
          slug: 'study-template-form',
        })
      );
    }
  };

  const handleDeleteSection = (id: string) => {
    dispatch({
      type: 'OPEN_MODAL',
      modal: 'CONFIRM_DELETE_STUDY_FORM_SECTION',
      props: {
        onClick: () => deleteSection(id),
      },
    });
  };

  const handleEditSection = (id: string) => {
    openSectionModal(id);
  };

  return (
    <>
      {_notNil(apiError) && <ApiErrorBanner error={apiError} />}
      <form onSubmit={handleSubmit(onSubmit)} className="pa4">
        <div className="mb3">
          <label htmlFor="name">Name</label>
          <input
            id="name"
            type="text"
            className={cn({ input__error: _notNil(errors?.name) })}
            {...register('name', {
              required: 'This field is required',
              minLength: {
                value: 3,
                message: 'Minimum length of 3 characters',
              },
              maxLength: {
                value: 50,
                message: 'Maximum length of 50 characters',
              },
            })}
            style={{ marginBottom: 0 }}
          />
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }) => <p className="f6 red db pt2">{message}</p>}
          />
        </div>
        <div className="mb3">
          <label htmlFor="type">Will the study be conducted internally or externally?</label>
          {(Object.keys(studyTypes) as Array<keyof typeof studyTypes>).map((key) => (
            <Controller
              key={key}
              name="type"
              control={control}
              render={({ field }) => (
                <Radio
                  value={key}
                  id={`study-type-${key}`}
                  name="type"
                  label={studyTypes[key] as string}
                  className="mb2"
                  checked={field.value === key}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          ))}
        </div>
        <div className="mb3 mw6">
          <label>Form sections</label>
          <div className="ui-card">
            <div className="pa3 bb b--moon-gray">
              <span className="db lh-copy pb3 f6">Information (required)</span>
              <span className="db lh-copy f6">Team (required)</span>
            </div>
            <div className="pa3 bb b--moon-gray">
              <FormProvider {...formMethods}>
                <DraggableList
                  canAmend={false}
                  data={fields}
                  sortable={{
                    droppableId: 'study-form-section-item',
                    onReorder: moveFormItems,
                  }}
                  renderItem={asSortableListItem({
                    canSort: true,
                    itemClassName: 'pb2 mb2',
                  })(FormItem, {
                    handleEditSection,
                    handleDeleteSection,
                  })}
                />
              </FormProvider>
            </div>
            <div className="pa3">
              <Button pill icon="add_new" paleBlue onClick={() => openSectionModal()}>
                Add section
              </Button>
            </div>
          </div>
        </div>
        <div className="pv3">
          <Button submit disabled={submitting}>
            Save
          </Button>
          <Button plain className="ml3" url={webRoute('team.studies.forms')} disabled={submitting}>
            Cancel
          </Button>
        </div>
      </form>
    </>
  );
};

interface FormItemProps {
  id: string;
  value: StudyFormSection;
  index: number;
  handleEditSection: (id: string) => void;
  handleDeleteSection: (id: string) => void;
}

const FormItem: FC<FormItemProps> = ({ value, index, handleEditSection, handleDeleteSection }) => {
  const { control } = useFormContext<StudyTemplateFormData>();
  const sections = useWatch({ control, name: 'sections' });
  const linkClasses = 'pointer f6 child ml3 dib lh-solid bn bg-transparent pa0';

  const isDisabled =
    (value.slug === 'animals' || value.slug === 'cages_animals') &&
    (sections?.some(
      (s) =>
        s.value.slug !== value.slug &&
        (s.value.slug === 'animals' || s.value.slug === 'cages_animals') &&
        s.value.active === true
    ) ??
      false);

  const getTooltipMessage = () => {
    const activeSection = sections?.find(
      (s) =>
        s.value.slug !== value.slug &&
        (s.value.slug === 'animals' || s.value.slug === 'cages_animals') &&
        s.value.active === true
    );
    return _notNil(activeSection) ? `This cannot be selected while "${activeSection.value.name}" is checked` : '';
  };
  return (
    <Controller
      key={index}
      defaultValue={value}
      name={`sections.${index}.value`}
      control={control}
      render={({ field: { onChange, value: fieldValue } }) => (
        <div className="flex justify-between items-center">
          <Tooltip render={isDisabled ? getTooltipMessage() : null}>
            <Checkbox
              className={cn('pointer', { pv1: index !== 0 })}
              label={fieldValue.name}
              name={String(fieldValue.id)}
              checked={fieldValue.active}
              disabled={isDisabled}
              onChange={() =>
                onChange({
                  ...fieldValue,
                  active: !fieldValue.active,
                })
              }
            />
          </Tooltip>
          {fieldValue.type === 'custom' && (
            <>
              <a className={cn(linkClasses, 'blue')} onClick={() => handleEditSection(String(fieldValue.id))}>
                Edit
              </a>
              <a className={cn(linkClasses, 'red')} onClick={() => handleDeleteSection(String(fieldValue.id))}>
                Delete
              </a>
            </>
          )}
        </div>
      )}
    />
  );
};

export default StudyTemplateForm;
